<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3'
import {
  Activity,
  ChevronDown,
  ChevronRight,
  Clipboard,
  ClipboardCheck,
  Trash2,
  X,
} from 'lucide-vue-next'
import { computed, onUnmounted, reactive, ref, watch } from 'vue'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppPagination from '@/components/AppPagination.vue'
import LogFilterBar from '@/components/LogFilterBar.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useCopyToClipboard } from '@/composables/useCopyToClipboard'
import { useDialog } from '@/composables/useDialog'
import AppLayout from '@/layouts/AppLayout.vue'
import { highlightJson } from '@/lib/syntax-highlight'
import { formatDateTime } from '@/lib/utils'
import { clearLenderApiLogsPath, lenderApiLogsPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'

interface JobEvent {
  id: number
  job: string
  event: string
  level: string
  message: string
  payload: Record<string, unknown>
  timestamp: string
}

interface LenderApiEntry {
  id: number
  lender: string
  method: string
  url: string
  req_headers: Record<string, string> | null
  req_body: unknown | null
  status: number | null
  resp_headers: Record<string, string> | null
  resp_body: unknown | null
  duration_ms: number | null
  timestamp: string
  job_events: JobEvent[]
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  lender?: string | null
  status?: string | null
  date_from?: string | null
  date_to?: string | null
}

const props = defineProps<{
  entries: LenderApiEntry[]
  lenders: string[]
  statuses: number[]
  pagination: PaginationMeta
  filters: Filters
}>()

const breadcrumbs: BreadcrumbItem[] = [{ title: 'Lender API Logs', href: lenderApiLogsPath() }]

const ALL = '__all__'

const search = ref(props.filters.q ?? '')
const lenderFilter = ref(props.filters.lender ?? ALL)
const statusFilter = ref(props.filters.status ?? ALL)
const dateFrom = ref(props.filters.date_from ?? '')
const dateTo = ref(props.filters.date_to ?? '')
const expandedId = ref<number | null>(null)
const openSections = reactive(new Set<string>())
const { copy, isCopied } = useCopyToClipboard()

function isSectionOpen(entryId: number, section: 'req' | 'resp') {
  return openSections.has(`${entryId}-${section}`)
}

function toggleSection(entryId: number, section: 'req' | 'resp') {
  const key = `${entryId}-${section}`
  if (openSections.has(key)) {
    openSections.delete(key)
  } else {
    openSections.add(key)
  }
}

const hasActiveFilters = computed(
  () =>
    search.value !== '' ||
    lenderFilter.value !== ALL ||
    statusFilter.value !== ALL ||
    dateFrom.value !== '' ||
    dateTo.value !== '',
)

const filterParams = computed(() => ({
  q: search.value || undefined,
  lender: lenderFilter.value !== ALL ? lenderFilter.value : undefined,
  status: statusFilter.value !== ALL ? statusFilter.value : undefined,
  date_from: dateFrom.value || undefined,
  date_to: dateTo.value || undefined,
}))

function applyFilters() {
  const params = Object.fromEntries(Object.entries(filterParams.value).filter(([, v]) => v != null))
  router.get(lenderApiLogsPath(), params, { preserveState: true, replace: true })
}

let searchTimer: ReturnType<typeof setTimeout>

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})

watch([lenderFilter, statusFilter, dateFrom, dateTo], applyFilters)

onUnmounted(() => clearTimeout(searchTimer))

function clearFilters() {
  search.value = ''
  lenderFilter.value = ALL
  statusFilter.value = ALL
  dateFrom.value = ''
  dateTo.value = ''
}

function toggleExpand(id: number) {
  expandedId.value = expandedId.value === id ? null : id
}

async function copyRequest(entry: LenderApiEntry) {
  const payload = { method: entry.method, url: entry.url, headers: entry.req_headers, body: entry.req_body }
  await copy(JSON.stringify(payload, null, 2), `${entry.id}-req`)
}

async function copyResponse(entry: LenderApiEntry) {
  const payload = { status: entry.status, headers: entry.resp_headers, body: entry.resp_body }
  await copy(JSON.stringify(payload, null, 2), `${entry.id}-resp`)
}


function statusVariant(status: number | null): 'default' | 'secondary' | 'destructive' {
  if (status === null) return 'secondary'
  if (status >= 200 && status < 300) return 'default'
  if (status >= 400 && status < 500) return 'secondary'
  return 'destructive'
}

function truncateUrl(url: string, maxLen = 60): string {
  if (!url || url.length <= maxLen) return url
  return url.substring(0, maxLen) + '…'
}

// ── Job events ───────────────────────────────────────────────────────────────

const JOB_LEVEL_CLASS: Record<string, string> = {
  info:  'border-transparent bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  warn:  'border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  error: 'border-transparent bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
}

function jobLevelClass(level: string) {
  return JOB_LEVEL_CLASS[level?.toLowerCase()] ?? JOB_LEVEL_CLASS.info
}

const JOB_EVENT_CLASS: Record<string, string> = {
  quotes_created:       'text-green-700 dark:text-green-400',
  quote_declined:       'text-yellow-700 dark:text-yellow-400',
  quote_failed:         'text-red-700 dark:text-red-400',
  pending_retry:        'text-indigo-700 dark:text-indigo-400',
  unknown_status_retry: 'text-orange-700 dark:text-orange-400',
  api_error:            'text-red-700 dark:text-red-400',
}

function jobEventClass(event: string) {
  return JOB_EVENT_CLASS[event] ?? 'text-foreground'
}

// ── Clear logs ───────────────────────────────────────────────────────────────
const { confirm } = useDialog()

async function doClears() {
  const ok = await confirm({
    title: 'Clear lender API logs?',
    description: 'This will permanently delete all log entries older than 3 days. This cannot be undone.',
    confirmLabel: 'Clear',
  })
  if (!ok) return
  router.delete(clearLenderApiLogsPath(), { preserveState: false })
}

const apiTableColumns: TableColumn[] = [
  { key: 'expand', label: '', headerClass: 'w-8' },
  { key: 'lender', label: 'Lender', headerClass: 'w-28' },
  { key: 'endpoint', label: 'Endpoint' },
  { key: 'status', label: 'Status', headerClass: 'w-20' },
  { key: 'timestamp', label: 'Timestamp', headerClass: 'w-40' },
]

</script>

<template>
  <Head title="Lender API Logs" />
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 p-4">
      <!-- Page header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Activity class="text-muted-foreground size-6" />
          <div>
            <h1 class="text-foreground text-xl font-semibold">Lender API Logs</h1>
            <p class="text-muted-foreground text-sm">Debug log of all outbound lender API requests and responses</p>
          </div>
        </div>
        <Button variant="outline" size="sm" class="gap-1.5 text-destructive hover:text-destructive" @click="doClears()">
          <Trash2 class="size-3.5" />
          Clear
        </Button>
      </div>

      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <div class="flex items-center justify-between">
            <div>
              <CardTitle class="text-base">All API Calls</CardTitle>
              <CardDescription>
                {{ pagination.total_count }} entry{{ pagination.total_count !== 1 ? 'ies' : 'y' }}
                {{ hasActiveFilters ? 'found' : 'total' }}
              </CardDescription>
            </div>
            <Activity class="text-muted-foreground size-4" />
          </div>
        </CardHeader>

        <!-- Filters -->
        <LogFilterBar
          v-model:search="search"
          v-model:date-from="dateFrom"
          v-model:date-to="dateTo"
          :has-active-filters="hasActiveFilters"
          search-placeholder="Search by lender or URL…"
          @clear="clearFilters"
        >
          <Select v-model="lenderFilter">
            <SelectTrigger class="w-full sm:w-44">
              <SelectValue placeholder="Any lender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any lender</SelectItem>
              <SelectItem v-for="l in lenders" :key="l" :value="l">{{ l }}</SelectItem>
            </SelectContent>
          </Select>

          <Select v-model="statusFilter">
            <SelectTrigger class="w-full sm:w-32">
              <SelectValue placeholder="Any status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any status</SelectItem>
              <SelectItem v-for="s in statuses" :key="s" :value="String(s)">{{ s }}</SelectItem>
            </SelectContent>
          </Select>
        </LogFilterBar>

        <CardContent class="p-0">
          <!-- Empty state -->
          <div v-if="entries.length === 0" class="flex flex-col items-center gap-3 py-16 text-center">
            <Activity class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">
              {{ hasActiveFilters ? 'No entries match your filters' : 'No API calls logged yet' }}
            </p>
            <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
              <X class="size-4" />
              Clear filters
            </Button>
          </div>

          <!-- Table -->
          <AppDataTable v-else :columns="apiTableColumns" table-fixed compact>
            <template v-for="entry in entries" :key="entry.id">
              <!-- Summary row -->
              <tr
                class="border-border cursor-pointer border-b transition-colors select-none"
                :class="expandedId === entry.id ? 'bg-muted/60' : 'hover:bg-muted/40'"
                @click="toggleExpand(entry.id)"
              >
                <td class="px-3 py-4 text-center">
                  <ChevronDown
                    v-if="expandedId === entry.id"
                    class="text-muted-foreground size-4 transition-transform"
                  />
                  <ChevronRight v-else class="text-muted-foreground size-4" />
                </td>
                <td class="px-4 py-4 font-medium">{{ entry.lender }}</td>
                <td class="px-4 py-4">
                  <span class="font-mono text-xs">
                    <Badge variant="outline" class="mr-1.5 font-mono text-[10px]">{{ entry.method }}</Badge>
                    <span class="text-muted-foreground">{{ truncateUrl(entry.url) }}</span>
                  </span>
                </td>
                <td class="px-4 py-4">
                  <Badge v-if="entry.status" :variant="statusVariant(entry.status)">{{ entry.status }}</Badge>
                  <span v-else class="text-muted-foreground text-xs">—</span>
                </td>
                <td class="text-muted-foreground py-4 whitespace-nowrap">
                  {{ formatDateTime(entry.timestamp) }}
                </td>
              </tr>

              <!-- Expanded detail row -->
              <tr v-if="expandedId === entry.id" class="border-border border-b">
                <td colspan="5" class="bg-muted/30 overflow-x-hidden px-6 py-5">
                  <div class="flex flex-col gap-5">
                    <!-- Request section -->
                    <div class="flex items-start gap-1">
                      <Collapsible
                        class="min-w-0 flex-1"
                        :open="isSectionOpen(entry.id, 'req')"
                        @update:open="toggleSection(entry.id, 'req')"
                      >
                        <CollapsibleTrigger as-child>
                          <button
                            class="hover:bg-muted/60 flex w-full items-center gap-2 rounded-md px-2 py-1.5 transition-colors"
                            @click.stop
                          >
                            <ChevronDown v-if="isSectionOpen(entry.id, 'req')" class="text-muted-foreground size-3.5" />
                            <ChevronRight v-else class="text-muted-foreground size-3.5" />
                            <h4 class="text-foreground text-xs font-semibold tracking-wide uppercase">Request</h4>
                            <Badge variant="outline" class="ml-1 font-mono text-[10px]">{{ entry.method }}</Badge>
                            <span class="text-muted-foreground truncate font-mono text-[11px]">{{ entry.url }}</span>
                          </button>
                        </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div class="mt-3 space-y-3 pl-6">
                          <div class="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3">
                            <div>
                              <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Method</p>
                              <p class="mt-0.5 text-sm font-medium">{{ entry.method }}</p>
                            </div>
                            <div class="col-span-2">
                              <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">URL</p>
                              <p class="mt-0.5 break-all font-mono text-xs">{{ entry.url }}</p>
                            </div>
                          </div>
                          <div v-if="entry.req_headers">
                            <p class="text-muted-foreground mb-1.5 text-xs font-medium tracking-wide uppercase">
                              Headers
                            </p>
                            <pre
                              class="bg-background border-border max-h-80 overflow-x-auto overflow-y-auto rounded-md border p-3 font-mono text-xs leading-relaxed"
                              v-html="highlightJson(entry.req_headers)"></pre
                            >
                          </div>
                          <div v-if="entry.req_body">
                            <p class="text-muted-foreground mb-1.5 text-xs font-medium tracking-wide uppercase">
                              Body
                            </p>
                            <pre
                              class="bg-background border-border max-h-80 overflow-x-auto overflow-y-auto rounded-md border p-3 font-mono text-xs leading-relaxed"
                              v-html="highlightJson(entry.req_body)"></pre
                            >
                          </div>
                        </div>
                      </CollapsibleContent>
                      </Collapsible>
                      <Button
                        v-if="isSectionOpen(entry.id, 'req')"
                        variant="ghost"
                        size="sm"
                        class="text-muted-foreground hover:text-foreground mt-0.5 h-6 shrink-0 gap-1.5 px-2 text-xs"
                        @click.stop="copyRequest(entry)"
                      >
                        <ClipboardCheck v-if="isCopied(`${entry.id}-req`)" class="size-3.5 text-green-600" />
                        <Clipboard v-else class="size-3.5" />
                        {{ isCopied(`${entry.id}-req`) ? 'Copied' : 'Copy' }}
                      </Button>
                    </div>

                    <!-- Response section -->
                    <div class="flex items-start gap-1">
                      <Collapsible
                        class="min-w-0 flex-1"
                        :open="isSectionOpen(entry.id, 'resp')"
                        @update:open="toggleSection(entry.id, 'resp')"
                      >
                        <CollapsibleTrigger as-child>
                          <button
                            class="hover:bg-muted/60 flex w-full items-center gap-2 rounded-md px-2 py-1.5 transition-colors"
                            @click.stop
                          >
                            <ChevronDown v-if="isSectionOpen(entry.id, 'resp')" class="text-muted-foreground size-3.5" />
                            <ChevronRight v-else class="text-muted-foreground size-3.5" />
                            <h4 class="text-foreground text-xs font-semibold tracking-wide uppercase">Response</h4>
                            <Badge v-if="entry.status" :variant="statusVariant(entry.status)" class="ml-1 text-[10px]">
                              {{ entry.status }}
                            </Badge>
                            <span v-else class="text-muted-foreground text-xs">No response</span>
                          </button>
                        </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div class="mt-3 space-y-3 pl-6">
                          <div>
                            <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Status</p>
                            <p class="mt-0.5">
                              <Badge v-if="entry.status" :variant="statusVariant(entry.status)">
                                {{ entry.status }}
                              </Badge>
                              <span v-else class="text-muted-foreground text-sm">No response</span>
                            </p>
                          </div>
                          <div v-if="entry.resp_headers">
                            <p class="text-muted-foreground mb-1.5 text-xs font-medium tracking-wide uppercase">
                              Headers
                            </p>
                            <pre
                              class="bg-background border-border max-h-80 overflow-x-auto overflow-y-auto rounded-md border p-3 font-mono text-xs leading-relaxed"
                              v-html="highlightJson(entry.resp_headers)"></pre
                            >
                          </div>
                          <div v-if="entry.resp_body">
                            <p class="text-muted-foreground mb-1.5 text-xs font-medium tracking-wide uppercase">
                              Body
                            </p>
                            <pre
                              class="bg-background border-border max-h-80 overflow-x-auto overflow-y-auto rounded-md border p-3 font-mono text-xs leading-relaxed"
                              v-html="highlightJson(entry.resp_body)"></pre
                            >
                          </div>
                        </div>
                      </CollapsibleContent>
                      </Collapsible>
                      <Button
                        v-if="isSectionOpen(entry.id, 'resp')"
                        variant="ghost"
                        size="sm"
                        class="text-muted-foreground hover:text-foreground mt-0.5 h-6 shrink-0 gap-1.5 px-2 text-xs"
                        @click.stop="copyResponse(entry)"
                      >
                        <ClipboardCheck v-if="isCopied(`${entry.id}-resp`)" class="size-3.5 text-green-600" />
                        <Clipboard v-else class="size-3.5" />
                        {{ isCopied(`${entry.id}-resp`) ? 'Copied' : 'Copy' }}
                      </Button>
                    </div>

                    <!-- Job events for this HTTP call -->
                    <div v-if="entry.job_events.length > 0" class="border-border/50 border-t pt-4">
                      <p class="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">Job Events</p>
                      <div class="space-y-2">
                        <div
                          v-for="ev in entry.job_events"
                          :key="ev.id"
                          class="bg-background border-border flex items-start gap-3 rounded-md border px-3 py-2"
                        >
                          <Badge variant="outline" :class="jobLevelClass(ev.level)" class="mt-0.5 shrink-0 text-[10px] uppercase">
                            {{ ev.level }}
                          </Badge>
                          <div class="min-w-0 flex-1">
                            <div class="flex items-baseline gap-2 flex-wrap">
                              <span class="text-xs font-medium" :class="jobEventClass(ev.event)">{{ ev.event }}</span>
                              <span class="text-muted-foreground text-[11px]">{{ ev.message }}</span>
                              <span class="text-muted-foreground ml-auto shrink-0 font-mono text-[10px]">{{ formatDateTime(ev.timestamp) }}</span>
                            </div>
                            <div v-if="Object.keys(ev.payload).length > 0" class="mt-1.5 flex flex-wrap gap-x-6 gap-y-1">
                              <div v-for="(val, key) in ev.payload" :key="key" class="text-[11px]">
                                <span class="text-muted-foreground">{{ key }}: </span>
                                <span class="font-mono">{{ val ?? '—' }}</span>
                              </div>
                            </div>
                          </div>
                          <Button
                            v-if="Object.keys(ev.payload).length > 0"
                            variant="ghost"
                            size="sm"
                            class="text-muted-foreground hover:text-foreground h-6 shrink-0 px-2"
                            @click.stop="copy(JSON.stringify(ev.payload, null, 2), `job-${ev.id}`)"
                          >
                            <ClipboardCheck v-if="isCopied(`job-${ev.id}`)" class="size-3.5 text-green-600" />
                            <Clipboard v-else class="size-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </AppDataTable>

          <!-- Pagination -->
          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="lenderApiLogsPath()"
            :extra-params="filterParams"
          />
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
